import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { WorkflowApiService } from './workflow-api.service';

@Component({
  standalone: true,
  selector: 'ain-workflow-intent-create',
  imports: [
    AinPageComponent,
    InfoHintComponent,
    FormsModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    SelectModule
  ],
  template: `
    <ain-page [title]="t('workflow.intents.create.title')" [description]="t('workflow.intents.create.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'workflow.intents.create.hint.title'"
        [descriptionKey]="'workflow.intents.create.hint.description'"
        [bulletKeys]="[
          'workflow.intents.create.hint.bullets.write',
          'workflow.intents.create.hint.bullets.process',
          'workflow.intents.create.hint.bullets.confirm'
        ]" />

      <form class="composer" (submit)="submit($event)">
        <label class="sr-only" for="intent-text">{{ t('workflow.intents.create.fields.text') }}</label>
        <textarea
          id="intent-text"
          pTextarea
          rows="6"
          [(ngModel)]="form.text"
          name="intentText"
          [placeholder]="t('workflow.intents.create.placeholders.text')"
          (keydown.control.enter)="submit($event)"
          (keydown.meta.enter)="submit($event)"></textarea>
        @if (errors().text) {
          <small class="error-text">{{ t(errors().text!) }}</small>
        }

        <div class="actions">
          <button pButton type="button" severity="secondary" [outlined]="true" [label]="t('common.cancel')" (click)="goBackToList()"></button>
          <button
            pButton
            type="submit"
            icon="pi pi-bolt"
            [label]="t('workflow.intents.create.actions.submit')"
            [loading]="submitting()"></button>
        </div>
      </form>

      @if (submitError()) {
        <p class="submit-error">{{ t(submitError()!) }}</p>
      }

      <button type="button" class="advanced-toggle" (click)="showAdvanced.set(!showAdvanced())">
        {{ t('workflow.intents.create.advanced.toggle') }}
        <i class="pi" [class]="showAdvanced() ? 'pi-chevron-up' : 'pi-chevron-down'" aria-hidden="true"></i>
      </button>

      @if (showAdvanced()) {
        <section class="advanced" [attr.aria-label]="t('workflow.intents.create.advanced.toggle')">
          <div class="field">
            <label for="intent-channel">{{ t('workflow.intents.create.fields.channel') }}</label>
            <p-select
              id="intent-channel"
              appendTo="body"
              [options]="channelOptions"
              optionLabel="label"
              optionValue="value"
              [placeholder]="t('workflow.intents.create.placeholders.channel')"
              [(ngModel)]="form.channel"
              [ngModelOptions]="{standalone: true}"></p-select>
          </div>
          <div class="field">
            <label for="intent-conversation">{{ t('workflow.intents.create.fields.conversationId') }}</label>
            <input id="intent-conversation" pInputText [(ngModel)]="form.conversationId" [ngModelOptions]="{standalone: true}" />
          </div>
          <div class="field">
            <label for="intent-correlation">{{ t('workflow.intents.create.fields.correlationId') }}</label>
            <input id="intent-correlation" pInputText [(ngModel)]="form.correlationId" [ngModelOptions]="{standalone: true}" />
          </div>
          <div class="field">
            <label for="intent-context">{{ t('workflow.intents.create.fields.contextJson') }}</label>
            <textarea
              id="intent-context"
              pTextarea
              rows="4"
              [(ngModel)]="form.contextJson"
              [ngModelOptions]="{standalone: true}"
              [placeholder]="t('workflow.intents.create.placeholders.contextJson')"></textarea>
            @if (errors().contextJson) {
              <small class="error-text">{{ t(errors().contextJson!) }}</small>
            }
          </div>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .composer { display: grid; gap: 12px; }
    .composer textarea {
      width: 100%;
      min-height: 160px;
      font-size: 1.05rem;
      line-height: 1.6;
    }
    .actions { display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }
    .advanced-toggle {
      margin-top: 20px;
      border: 0;
      background: transparent;
      color: #64748b;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 0;
    }
    .advanced {
      margin-top: 12px;
      display: grid;
      gap: 12px;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    }
    .field { display: flex; flex-direction: column; gap: 6px; }
    .error-text, .submit-error { color: #dc2626; margin: 0; }
    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
    }
  `]
})
export class WorkflowIntentCreateComponent implements OnInit {
  private readonly api = inject(WorkflowApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly localization = inject(LocalizationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly submitting = signal(false);
  readonly submitError = signal<string | null>(null);
  readonly showAdvanced = signal(false);
  readonly errors = signal<{ text?: string; contextJson?: string }>({});

  readonly channelOptions = [
    { label: this.t('workflow.intents.create.channels.adminConsole'), value: 'admin-console' },
    { label: this.t('workflow.intents.create.channels.api'), value: 'api' },
    { label: this.t('workflow.intents.create.channels.chat'), value: 'chat' },
    { label: this.t('workflow.intents.create.channels.voice'), value: 'voice' }
  ];

  readonly form: {
    text: string;
    channel: string;
    conversationId: string;
    correlationId: string;
    contextJson: string;
  } = {
      text: '',
      channel: 'admin-console',
      conversationId: '',
      correlationId: '',
      contextJson: '{}'
    };

  ngOnInit(): void {
    const preset = this.route.snapshot.queryParamMap.get('q')?.trim();
    if (preset) {
      this.form.text = preset;
    }
  }

  submit(event?: Event): void {
    event?.preventDefault();
    if (this.submitting()) {
      return;
    }
    this.submitError.set(null);
    const validation = this.validate();
    this.errors.set(validation);
    if (validation.text || validation.contextJson) {
      if (validation.contextJson) {
        this.showAdvanced.set(true);
      }
      return;
    }

    this.submitting.set(true);
    this.api.planIntent({
      text: this.form.text.trim(),
      channel: this.form.channel,
      locale: this.localization.activeCulture(),
      conversationId: this.normalizeOptional(this.form.conversationId),
      correlationId: this.normalizeOptional(this.form.correlationId),
      contextJson: this.form.contextJson.trim() || '{}'
    }).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.submitting.set(false))
    ).subscribe({
      next: result => {
        void this.router.navigate(['/workflow/intents', result.intentRequestId]);
      },
      error: error => {
        this.submitError.set(this.resolveErrorKey(error?.error?.code ?? error?.error?.Error));
      }
    });
  }

  goBackToList(): void {
    void this.router.navigate(['/workflow/intents']);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private validate(): { text?: string; contextJson?: string } {
    const next: { text?: string; contextJson?: string } = {};
    if (!this.form.text.trim()) {
      next.text = 'workflow.intents.create.validation.textRequired';
    }

    if (this.form.contextJson.trim()) {
      try {
        JSON.parse(this.form.contextJson);
      } catch {
        next.contextJson = 'workflow.intents.create.validation.contextJsonInvalid';
      }
    }

    return next;
  }

  private normalizeOptional(value: string): string | undefined {
    const normalized = value.trim();
    return normalized ? normalized : undefined;
  }

  private resolveErrorKey(errorCode: string | undefined): string {
    if (!errorCode) {
      return 'common.saveFailed';
    }

    const localized = this.localization.translate(errorCode);
    return localized === errorCode ? 'common.saveFailed' : errorCode;
  }
}
